import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import useRequests from "./http";
import { useAuth } from "./useAuth";

interface TenantContextType {
  tenants: Tenant[];
  activeTenant: Tenant | null;
  setActiveTenant: (tenant: Tenant) => Promise<void>;
  addTenant: (tenant: Tenant) => void;
  updateTenant: (tenant: Tenant) => void;
  removeTenant: (tenantId: number) => void;
  loading: boolean;
}

const TenantContext = createContext<TenantContextType | undefined>(undefined);

const STORAGE_KEY = "janus_active_tenant_id";

export function useTenant() {
  const context = useContext(TenantContext);
  if (!context) {
    throw new Error("useTenant must be used within a TenantProvider");
  }
  return context;
}

export const TenantProvider = ({ children }: { children: React.ReactNode }) => {
  const { user, refresh } = useAuth();
  const { get, post } = useRequests();
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [activeTenant, setActiveTenantState] = useState<Tenant | null>(null);
  const [loading, setLoading] = useState(true);

  const setActiveTenant = useCallback(
    async (tenant: Tenant) => {
      const { status } = await post("/platform/self/switch-tenant", null, {
        tenant_id: tenant.id,
      });
      if (status !== 200) return;
      setActiveTenantState(tenant);
      localStorage.setItem(STORAGE_KEY, String(tenant.id));
      await refresh();
    },
    [post, refresh],
  );

  const addTenant = useCallback((tenant: Tenant) => {
    setTenants((prev) => [...prev, tenant]);
    setActiveTenantState(tenant);
    localStorage.setItem(STORAGE_KEY, String(tenant.id));
  }, []);

  const updateTenant = useCallback((tenant: Tenant) => {
    setTenants((prev) => prev.map((t) => (t.id === tenant.id ? tenant : t)));
    setActiveTenantState((prev) => (prev?.id === tenant.id ? tenant : prev));
  }, []);

  const removeTenant = useCallback((tenantId: number) => {
    setTenants((prev) => prev.filter((t) => t.id !== tenantId));
    setActiveTenantState((prev) => {
      if (prev?.id !== tenantId) return prev;
      return null;
    });
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved && Number(saved) === tenantId) {
      localStorage.removeItem(STORAGE_KEY);
    }
  }, []);

  const initializedRef = useRef(false);

  useEffect(() => {
    if (!user) {
      setTenants([]);
      setActiveTenantState(null);
      setLoading(false);
      initializedRef.current = false;
      return;
    }

    if (initializedRef.current) return;
    initializedRef.current = true;

    get("/platform/self/memberships")
      .then(async ({ data, status }) => {
        if (status === 200 && Array.isArray(data)) {
          setTenants(data);

          // The JWT is the source of truth for the active tenant. user.tid is
          // the GUID of the tenant the backend currently considers active, so
          // the displayed workspace must be the membership matching it.
          const byJwt = user.tid
            ? data.find((t: Tenant) => t.guid === user.tid)
            : null;

          if (byJwt) {
            setActiveTenantState(byJwt);
            localStorage.setItem(STORAGE_KEY, String(byJwt.id));
            setLoading(false);
            return;
          }

          // No tenant resolved on the JWT yet: fall back to the saved choice,
          // otherwise the first membership, and switch the backend to match.
          const savedId = localStorage.getItem(STORAGE_KEY);
          const saved = savedId
            ? data.find((t: Tenant) => t.id === Number(savedId))
            : null;
          const chosen = saved ?? data[0] ?? null;
          setActiveTenantState(chosen);

          if (chosen) {
            await post("/platform/self/switch-tenant", null, {
              tenant_id: chosen.id,
            });
            await refresh();
          }
        }
        setLoading(false);
      })
      .catch(() => {
        setLoading(false);
      });
  }, [user, get, post, refresh]);

  const value = useMemo(
    () => ({
      tenants,
      activeTenant,
      setActiveTenant,
      addTenant,
      updateTenant,
      removeTenant,
      loading,
    }),
    [
      tenants,
      activeTenant,
      setActiveTenant,
      addTenant,
      updateTenant,
      removeTenant,
      loading,
    ],
  );

  return (
    <TenantContext.Provider value={value}>{children}</TenantContext.Provider>
  );
};
